import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { MapPin, MessageCircle, PhoneCall, Send, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CONTACT, DEFAULT_WHATSAPP_MESSAGE, getWhatsAppUrl, isWorkshopPagePath, SOCIAL_LINKS } from "./config";
import { CookieSettingsLink } from "./CookieConsent";
import { submitContactIntent } from "./analytics";
import { EditableText } from "./TextEditor";

const initialForm = {
  nome: "",
  telefono: "",
  veicolo: "",
  problema: "",
  posizione: "",
  note: "",
};

const socialIconMap = {
  facebook: (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M13.5 21v-7h2.4l.4-2.8h-2.8V9.4c0-.8.2-1.4 1.4-1.4H16V5.5c-.2 0-.9-.1-1.8-.1-1.8 0-3.1 1.1-3.1 3.3v1.9H9v2.8h2.3v7h2.2Z" />
    </svg>
  ),
  instagram: (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M7.5 3h9A4.5 4.5 0 0 1 21 7.5v9a4.5 4.5 0 0 1-4.5 4.5h-9A4.5 4.5 0 0 1 3 16.5v-9A4.5 4.5 0 0 1 7.5 3Zm0 1.8A2.7 2.7 0 0 0 4.8 7.5v9a2.7 2.7 0 0 0 2.7 2.7h9a2.7 2.7 0 0 0 2.7-2.7v-9a2.7 2.7 0 0 0-2.7-2.7h-9Zm9.45 1.35a1.05 1.05 0 1 1 0 2.1 1.05 1.05 0 0 1 0-2.1ZM12 7.5A4.5 4.5 0 1 1 7.5 12 4.5 4.5 0 0 1 12 7.5Zm0 1.8A2.7 2.7 0 1 0 14.7 12 2.7 2.7 0 0 0 12 9.3Z" />
    </svg>
  ),
  tiktok: (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M14.9 3c.3 2 1.5 3.6 3.4 4.5.8.4 1.7.6 2.7.6v2.8c-1.4 0-2.8-.3-4-.9v5.5a5.5 5.5 0 1 1-5.5-5.5c.4 0 .8 0 1.2.1v2.9a2.7 2.7 0 1 0 1.9 2.5V3h2.3Z" />
    </svg>
  ),
};

const FooterSocialLinks = ({ compact = false }) => (
  <div data-testid="footer-social-links" className={`footer-socials ${compact ? "footer-socials--compact" : ""}`.trim()}>
    <div data-testid="footer-social-list" className="footer-socials__list">
      {SOCIAL_LINKS.map((item) => (
        <a
          data-testid={`footer-social-link-${item.key}`}
          key={item.key}
          href={item.href}
          target="_blank"
          rel="noreferrer"
          className={`footer-socials__link footer-socials__link--${item.key}`}
          aria-label={item.label}
          title={item.label}
        >
          <span className="footer-socials__icon">{socialIconMap[item.key]}</span>
        </a>
      ))}
    </div>
  </div>
);

export const WhatsAppRequestForm = () => {
  const [form, setForm] = useState(initialForm);

  const message = useMemo(() => {
    return [
      "Salve Viscar, ho bisogno di intervento H24.",
      `Nome: ${form.nome || "-"}`,
      `Telefono: ${form.telefono || "-"}`,
      `Tipo veicolo: ${form.veicolo || "-"}`,
      `Problema: ${form.problema || "-"}`,
      `Posizione: ${form.posizione || "-"}`,
      `Note: ${form.note || "-"}`,
      "Richiedo assistenza.",
    ].join("\n");
  }, [form]);

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const handleContactIntent = () => {
    submitContactIntent({
      name: form.nome,
      phone: form.telefono,
      vehicle: form.veicolo,
      problem: form.problema,
      position: form.posizione,
      notes: form.note,
      page_path: window.location.pathname,
      submitted_via: "whatsapp_form",
    });
  };

  return (
    <section id="richiesta" data-testid="whatsapp-request-section" className="section request-section">
      <div className="container request-grid">
        <div data-testid="request-form-card" className="request-card">
          <EditableText textId="request.form.eyebrow" defaultText="Richiesta intervento rapido" as="span" testId="request-form-eyebrow" className="section-eyebrow" />
          <EditableText textId="request.form.title" defaultText="Richiesta operativa H24" as="h2" testId="request-form-title" />
          <EditableText textId="request.form.description" defaultText="Compila i dati essenziali per inviare subito una richiesta operativa via WhatsApp alla centrale Viscar." as="p" testId="request-form-description" multiline />

          <div data-testid="request-form-fields" className="request-form">
            <Input data-testid="request-name-input" aria-label="Nome" placeholder="Nome" value={form.nome} onChange={(event) => updateField("nome", event.target.value)} />
            <Input data-testid="request-phone-input" aria-label="Telefono" placeholder="Telefono" value={form.telefono} onChange={(event) => updateField("telefono", event.target.value)} />
            <Select value={form.veicolo} onValueChange={(value) => updateField("veicolo", value)}>
              <SelectTrigger data-testid="request-vehicle-select" aria-label="Tipo veicolo"><SelectValue placeholder="Tipo veicolo" /></SelectTrigger>
              <SelectContent data-testid="request-vehicle-options" className="select-dark">
                {['Auto', 'SUV', 'Furgone', 'Camion', 'Autocarro', 'Autobus', 'Semirimorchio'].map((item) => <SelectItem data-testid={`request-vehicle-option-${item.toLowerCase()}`} key={item} value={item}>{item}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={form.problema} onValueChange={(value) => updateField("problema", value)}>
              <SelectTrigger data-testid="request-problem-select" aria-label="Tipo problema"><SelectValue placeholder="Tipo problema" /></SelectTrigger>
              <SelectContent data-testid="request-problem-options" className="select-dark">
                {['Guasto', 'Incidente', 'Gomma autocarro', 'Mezzo bloccato', 'Recupero complesso', 'Altro'].map((item) => <SelectItem data-testid={`request-problem-option-${item.toLowerCase().replaceAll(" ", "-")}`} key={item} value={item}>{item}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid="request-position-input" aria-label="Posizione" className="request-form__full" placeholder="Posizione / tratto stradale" value={form.posizione} onChange={(event) => updateField("posizione", event.target.value)} />
            <Textarea data-testid="request-notes-textarea" aria-label="Note" className="request-form__full" placeholder="Note rapide: problema, immagini da inviare in chat, condizioni del mezzo..." value={form.note} onChange={(event) => updateField("note", event.target.value)} />
          </div>

          <div data-testid="request-form-actions" className="section-actions">
            <Button data-testid="request-whatsapp-submit" asChild className="btn-whatsapp btn-lg">
              <a href={getWhatsAppUrl(message)} target="_blank" rel="noreferrer" onClick={handleContactIntent}><Send size={18} /> <EditableText textId="request.form.cta.whatsapp" defaultText="Invia richiesta su WhatsApp" as="span" mode="click" /></a>
            </Button>
            <Button data-testid="request-call-button" asChild className="btn-ghost-tech btn-lg">
              <a href={CONTACT.roadsideHref}><PhoneCall size={18} /> <EditableText textId="request.form.cta.call" defaultText="Chiama invece" as="span" mode="click" /></a>
            </Button>
          </div>
        </div>

        <aside data-testid="request-summary-card" className="request-summary">
          <span data-testid="request-summary-live" className="live-label"><span /> <EditableText textId="request.summary.live" defaultText="Operatività H24" as="span" mode="click" /></span>
          <EditableText textId="request.summary.title" defaultText="Road assistance, truck support e recovery H24." as="h3" testId="request-summary-title" />
          <EditableText textId="request.summary.description" defaultText="Soccorso stradale H24, supporto mezzi pesanti, officina mobile e recuperi complessi su rete urbana e autostradale." as="p" testId="request-summary-description" multiline />
          <div data-testid="request-summary-list" className="summary-list">
            <div><ShieldCheck /> <EditableText textId="request.summary.item.0" defaultText="H24 Operation" as="span" mode="click" /></div>
            <div><MapPin /> <EditableText textId="request.summary.item.1" defaultText="Messina • A18 • A20" as="span" mode="click" /></div>
            <div><MessageCircle /> <EditableText textId="request.summary.item.2" defaultText="Emergency Support" as="span" mode="click" /></div>
          </div>
        </aside>
      </div>
    </section>
  );
};

export const MobileEmergencyBar = () => {
  const location = useLocation();
  const isWorkshopPage = isWorkshopPagePath(location.pathname);
  const callHref = isWorkshopPage ? CONTACT.workshopHref : CONTACT.roadsideHref;
  const whatsappHref = getWhatsAppUrl(DEFAULT_WHATSAPP_MESSAGE, isWorkshopPage ? CONTACT.workshopWhatsAppNumber : CONTACT.roadsideWhatsAppNumber);
  const isRoadsideHome = location.pathname === "/" || location.pathname === "/soccorso-h24";

  return (
    <nav data-testid="mobile-emergency-bar" className={`mobile-emergency-bar ${isRoadsideHome ? "mobile-emergency-bar--compact" : ""}`.trim()} aria-label="Azioni emergenza mobile">
      <a data-testid="mobile-bar-call" href={callHref}><PhoneCall size={20} /><EditableText textId="mobile.bar.call" defaultText="Chiama" as="span" mode="click" /></a>
      <a data-testid="mobile-bar-whatsapp" href={whatsappHref} target="_blank" rel="noreferrer"><MessageCircle size={20} /><EditableText textId="mobile.bar.whatsapp" defaultText="WhatsApp" as="span" mode="click" /></a>
      {isRoadsideHome ? null : <a data-testid="mobile-bar-request" href="#richiesta"><MapPin size={20} /><EditableText textId="mobile.bar.request" defaultText="Richiedi" as="span" mode="click" /></a>}
    </nav>
  );
};

export const Footer = () => {
  const location = useLocation();
  const isWorkshopPage = isWorkshopPagePath(location.pathname);
  const isContactsPage = location.pathname === "/contatti";

  const brandLines = (() => {
    if (isContactsPage) {
      return [
        { key: "roadside", textId: "footer.brand.sub.roadside", defaultText: "Soccorso Stradale H24", className: "brand-mark__sub" },
        { key: "workshop", textId: "footer.brand.sub.workshop", defaultText: "Officina Car & Truck", className: "brand-mark__sub brand-mark__sub--workshop" },
      ];
    }

    if (isWorkshopPage) {
      return [
        { key: "workshop", textId: "footer.brand.sub.workshop", defaultText: "Officina Car & Truck", className: "brand-mark__sub brand-mark__sub--workshop" },
      ];
    }

    return [
      { key: "roadside", textId: "footer.brand.sub.roadside", defaultText: "Road Assistance H24", className: "brand-mark__sub" },
    ];
  })();

  const footerSummary = (() => {
    if (location.pathname === "/meccatronica") {
      return {
        textId: "footer.seo.text.meccatronica",
        defaultText: "Reparto officina Viscar dedicato a diagnosi elettronica, centraline, manutenzione e supporto tecnico per auto, furgoni e mezzi da lavoro.",
      };
    }

    if (location.pathname === "/gommista") {
      return {
        textId: "footer.seo.text.gommista",
        defaultText: "Servizio gomme Viscar per auto, furgoni, autocarri e mezzi da lavoro con supporto pneumatici e assistenza tecnica di officina.",
      };
    }

    if (isContactsPage) {
      return {
        textId: "footer.seo.text.contatti",
        defaultText: "In questa pagina trovi i riferimenti diretti di Viscar per officina, meccatronica, servizio gomme e soccorso stradale H24 a Messina.",
      };
    }

    return {
      textId: "footer.seo.text.roadside",
      defaultText: "Base operativa Viscar a Messina e provincia con carroattrezzi e soccorso stradale H24, copertura A18 e A20, mezzi pesanti, officina mobile e recuperi complessi.",
    };
  })();

  return (
    <footer id="richiesta" data-testid="footer" className="footer">
      <div className="container footer-grid">
        <div data-testid="footer-brand" className="brand-mark brand-mark--footer">
          <EditableText textId="footer.brand.name" defaultText="VISCAR" as="span" className="brand-mark__name" mode="click" />
          <span data-testid="footer-brand-subtitles" className={`brand-mark__subtitles ${brandLines.length > 1 ? "brand-mark__subtitles--stacked" : ""}`}>
            {brandLines.map((line, index) => (
              <EditableText
                key={line.key}
                textId={line.textId}
                defaultText={line.defaultText}
                as="span"
                className={line.className}
                mode="click"
                testId={`footer-brand-subtitle-${line.key}-${index}`}
              />
            ))}
          </span>
        </div>
        <EditableText textId={footerSummary.textId} defaultText={footerSummary.defaultText} as="p" testId="footer-seo-text" multiline />
        {isContactsPage ? <div /> : <div data-testid="footer-side" className="footer-side"><FooterSocialLinks /><div className="footer-legal-links"><a data-testid="footer-privacy-link" href="/privacy-policy">Privacy Policy</a><a data-testid="footer-cookie-link" href="/cookie-policy">Cookie Policy</a><CookieSettingsLink /></div></div>}
      </div>
      <div data-testid="footer-credit" className="footer-credit">
        <span data-testid="footer-credit-line-1">Powered by Viscar ©</span>
        <span data-testid="footer-credit-line-2">Tutti i diritti riservati</span>
      </div>
    </footer>
  );
};